import * as React from "react";
import {
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    Input,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Select,
} from "@jhool-io/fe-components";
import { useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import PaymentsSummary from "../../../payments/components/PaymentsSummary/PaymentsSummary";
import AccountStatementList from "../AccountStatementList/AccountStatementList";
import FilterButton from "../../../../components/FilterButton/FilterButton";
import {
    convertDateFilterStringToDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import {
    useExportPdfs,
    useFetchStatementOfAccount,
    useGetDownloadJobId,
} from "../../hooks/statement-of-account.queries";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import ListState from "../../../../components/ListState/ListState";
import { IAppCustomDates } from "../../../../utils/types";
import useToast from "../../../../hooks/useToast";
import { useSendStatementOfAccount } from "../../hooks/statement-of-account.mutations";
import { useGetOutstandingBills } from "../../../billing/hooks/billing.queries";
import { useFetchUserDetails } from "../../../auth/hooks/auth.queries";

type Option = {
    label: React.ReactNode;
    value: string;
};

type Dialogs = "send-statement" | "loader";

export default function StatementOfAccount() {
    const [isDownloading, setIsDownloading] = React.useState(false);
    const [jobId, setJobId] = React.useState<string | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [dialogInView, setDialogInView] = React.useState<Dialogs | null>(
        null
    );

    const loggedInUser = useFetchUserDetails();

    const { toast } = useToast();

    const handleDialogInView = (modal: Dialogs | null) => {
        setDialogInView(() => modal);
    };

    const sendStatement = useSendStatementOfAccount({
        action: "email",
        custom_date: searchParams.get("custom_date") || undefined,
        from_date: searchParams.get("from_date"),
        to_date: searchParams.get("to_date"),
    });

    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");
    const fromDateFilterToDate = convertDateFilterStringToDate(fromDateFilter);
    const toDateFilterToDate = convertDateFilterStringToDate(toDateFilter);
    const coInsuranceFromFilter =
        searchParams.get("coInsurance_from") || undefined;
    const coInsuranceToFilter = searchParams.get("coInsurance_to") || undefined;
    const insuranceFromFilter = searchParams.get("insurance_from") || undefined;
    const insuranceToFilter = searchParams.get("insurance_to") || undefined;
    const onlyCoinsFilter = searchParams.get("only_coins") || "false";

    const customDateFilter =
        searchParams.get("custom_date") || IAppCustomDates.ALL_TIME;

    const { data, error, isLoading, isSuccess } = useFetchStatementOfAccount({
        action: "preview",
        from_date: fromDateFilter,
        to_date: toDateFilter,
        custom_date:
            customDateFilter !== "custom" ? customDateFilter : undefined,
        from_amount_outstanding_coins: coInsuranceFromFilter,
        to_amount_outstanding_coins: coInsuranceToFilter,
        from_amount_outstanding_ins: insuranceFromFilter,
        to_amount_outstanding_ins: insuranceToFilter,
        only_coins_outstanding: onlyCoinsFilter,
    });

    const outstandingBillsData = useGetOutstandingBills({});

    const customDateOptions = [
        {
            label: "Custom",
            value: "custom",
        },
        ...Object.values(IAppCustomDates).map((item) => ({
            label: makeStringFirstLetterCapital(removeEnumUnderscore(item)),
            value: item,
        })),
    ];

    // const amountDue = ["0", "20000", "40000", "60000", "80000", "100000"];

    // const amountOptions = amountDue.map((amount) => ({
    //     label: showMoneyInAppFormat(amount),
    //     value: amount,
    // }));

    // const coinsFromAmountOptions = [
    //     { label: "--", value: "" },
    //     ...amountOptions,
    // ];

    // const coinsToAmountOptions = [{ label: "--", value: "" }, ...amountOptions];

    // const insFromAmountOptions = [{ label: "--", value: "" }, ...amountOptions];

    // const insToAmountOptions = [{ label: "--", value: "" }, ...amountOptions];

    const statementDownload = useGetDownloadJobId(
        {
            action: "download",
            from_date: searchParams.get("from_date")
                ? format(
                      new Date(searchParams.get("from_date") as string),
                      "yyyy-MM-dd"
                  )
                : null,
            to_date: searchParams.get("to_date")
                ? format(
                      new Date(searchParams.get("to_date") as string),
                      "yyyy-MM-dd"
                  )
                : null,
            custom_date:
                (searchParams.get("custom_date") as IAppCustomDates) ||
                undefined,
        },
        isDownloading
    );

    const exportPdf = useExportPdfs(
        {
            export_job_id: jobId as string,
            pdf_type: "statement_of_account",
        },
        Boolean(jobId) && dialogInView === "loader"
    );

    const handleSendEmail = () => {
        sendStatement.mutate(null, {
            onSuccess: () => {
                toast({
                    mode: "success",
                    message: "Statement of account sent successfully",
                });
                handleDialogInView(null);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message ||
                        "Could not send statement of account at this time",
                });
            },
        });
    };

    const handleDownload = () => {
        setIsDownloading(true);
        if (statementDownload.error) {
            toast({
                mode: "error",
                message:
                    statementDownload.error.response?.data.message ||
                    "Could not download statement of account.",
            });
        }

        handleDialogInView("loader");
    };

    const handleFromDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("from_date");
        else searchParams.set("from_date", format(date, "yyyy-MM-dd"));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    const handleToDateFilterChange = (date: Date | null) => {
        if (date === null) searchParams.delete("to_date");
        else searchParams.set("to_date", format(date, "yyyy-MM-dd"));
        setSearchParams(searchParams);
    };

    const handleCustomDateChange = (value: string) => {
        if (value === IAppCustomDates.ALL_TIME)
            searchParams.delete("custom_date");
        else searchParams.set("custom_date", value);
        if (value !== "custom") {
            searchParams.delete("from_date");
            searchParams.delete("to_date");
        }
        setSearchParams(searchParams);
    };

    // const handleCoInsuranceFromChange = (value: string) => {
    //     if (value === "") searchParams.delete("coInsurance_from");
    //     else searchParams.set("coInsurance_from", value);
    //     setSearchParams(searchParams);
    // };

    // const handleCoInsuranceToChange = (value: string) => {
    //     if (value === "") searchParams.delete("coInsurance_to");
    //     else searchParams.set("coInsurance_to", value);
    //     setSearchParams(searchParams);
    // };

    // const handleInsuranceFromChange = (value: string) => {
    //     if (value === "") searchParams.delete("insurance_from");
    //     else searchParams.set("insurance_from", value);
    //     setSearchParams(searchParams);
    // };

    // const handleInsuranceToChange = (value: string) => {
    //     if (value === "") searchParams.delete("insurance_to");
    //     else searchParams.set("insurance_to", value);
    //     setSearchParams(searchParams);
    // };

    // const handleCoinsOnlyFilter = (value: string) => {
    //     if (value === "false") searchParams.delete("only_coins");
    //     else searchParams.set("only_coins", value);
    //     setSearchParams(searchParams);
    // };

    React.useEffect(() => {
        const isExportPdfLoading = exportPdf.isLoading;

        if (statementDownload.isSuccess) {
            setIsDownloading(false);
        }

        if (jobId && !isExportPdfLoading) {
            handleDialogInView(null);
            setJobId(null);
        }
    }, [statementDownload.isSuccess, jobId, exportPdf.isLoading]);

    React.useEffect(() => {
        if (statementDownload.data?.data.export_job_id) {
            const timeoutId = setTimeout(() => {
                setJobId(statementDownload.data?.data.export_job_id);
            }, 4000);

            return () => {
                clearTimeout(timeoutId);
            };
        }

        return () => {};
    }, [statementDownload.data?.data.export_job_id, statementDownload.isStale]);

    return (
        <div className="page">
            <Dialog open={dialogInView === "loader"}>
                <DialogContent
                    title="Download in progress"
                    variant="center"
                    handleCloseDialog={() => handleDialogInView(null)}
                    showFooter={false}
                >
                    <div className="loader">
                        <div className="loader-icon" />
                        <div className="loader-text">
                            Please wait while the download is in progress.
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <Dialog
                open={dialogInView === "send-statement" && loggedInUser.data}
            >
                <DialogContent
                    title="Send statement"
                    variant="center"
                    handleCloseDialog={() => handleDialogInView(null)}
                    onSaveClick={() => handleSendEmail()}
                    saveText="Send statement"
                >
                    <>
                        <p className=" mb-16 text-sm text-gray font-normal">
                            Your registered email is displayed below. Click
                            &quot;Send statement&quot; to send your statement of
                            account to this email.
                        </p>

                        <Input
                            disabled
                            label="Email address"
                            defaultValue={loggedInUser.data?.data.email}
                        />
                    </>
                </DialogContent>
            </Dialog>
            <div className="flex justify-between bg-white p-16 rounded-r8 mb-24 flex-wrap gap-24">
                <div className="flex items-center gap-x-12 gap-y-24 flex-wrap">
                    <Popover>
                        <PopoverTrigger asChild>
                            <FilterButton text="Filter by Date of service" />
                        </PopoverTrigger>
                        <PopoverContent className="p-16 gap-y-12 w-[220px]">
                            <Select
                                options={customDateOptions}
                                onChange={(val) =>
                                    handleCustomDateChange(
                                        (val as Option).value
                                    )
                                }
                                placeholder=""
                                label="Date range"
                                defaultValue={customDateOptions.find(
                                    (option) =>
                                        option.value === customDateFilter
                                )}
                            />
                            {customDateFilter === "custom" ? (
                                <>
                                    <DatePicker
                                        label="From"
                                        placeholderText="MM/DD/YYYY"
                                        selected={fromDateFilterToDate}
                                        onChange={handleFromDateFilterChange}
                                        maxDate={
                                            toDateFilterToDate ||
                                            new Date(Date.now())
                                        }
                                        isClearable
                                    />

                                    <DatePicker
                                        label="To"
                                        placeholderText="MM/DD/YYYY"
                                        selected={toDateFilterToDate}
                                        onChange={handleToDateFilterChange}
                                        minDate={fromDateFilterToDate}
                                        isClearable
                                    />
                                </>
                            ) : null}
                        </PopoverContent>
                    </Popover>

                    {/* <Popover>
                        <PopoverTrigger asChild>
                            <FilterButton
                                text="Session Fee Due"
                                stroke="#0b132b"
                            />
                        </PopoverTrigger>
                        <PopoverContent align="start" className="px-16 w-60">
                            <div className="flex flex-col gap-y-12">
                                <Select
                                    options={coinsFromAmountOptions}
                                    onChange={(val) =>
                                        handleCoInsuranceFromChange(
                                            (val as Option).value
                                        )
                                    }
                                    placeholder=""
                                    label="From"
                                    defaultValue={coinsFromAmountOptions.find(
                                        (option) =>
                                            option.value ===
                                            coInsuranceFromFilter
                                    )}
                                />
                                <Select
                                    options={coinsToAmountOptions}
                                    onChange={(val) =>
                                        handleCoInsuranceToChange(
                                            (val as Option).value
                                        )
                                    }
                                    placeholder=""
                                    label="To"
                                    defaultValue={coinsToAmountOptions.find(
                                        (option) =>
                                            option.value === coInsuranceToFilter
                                    )}
                                />
                            </div>
                        </PopoverContent>
                    </Popover>

                    <Popover>
                        <PopoverTrigger asChild>
                            <FilterButton
                                text="Insurance Due"
                                stroke="#0b132b"
                            />
                        </PopoverTrigger>
                        <PopoverContent align="start" className="px-16 w-60">
                            <div className="flex flex-col gap-y-12 ">
                                <Select
                                    options={insFromAmountOptions}
                                    onChange={(val) =>
                                        handleInsuranceFromChange(
                                            (val as Option).value
                                        )
                                    }
                                    placeholder=""
                                    label="From"
                                    defaultValue={insFromAmountOptions.find(
                                        (option) =>
                                            option.value === insuranceFromFilter
                                    )}
                                />

                                <Select
                                    options={insToAmountOptions}
                                    onChange={(val) =>
                                        handleInsuranceToChange(
                                            (val as Option).value
                                        )
                                    }
                                    placeholder=""
                                    label="To"
                                    defaultValue={insToAmountOptions.find(
                                        (option) =>
                                            option.value === insuranceToFilter
                                    )}
                                />
                            </div>
                        </PopoverContent>
                    </Popover>
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton text="Session Fee only" />
                        </DropdownTrigger>
                        <DropdownContent
                            align="start"
                            width={200}
                            maxHeight={216}
                            className="overflow-y-auto"
                        >
                            <DropdownItem
                                isSelected={!onlyCoinsFilter}
                                onClick={() => handleCoinsOnlyFilter("false")}
                            >
                                Show All
                            </DropdownItem>
                            <DropdownItem
                                isSelected={onlyCoinsFilter === "true"}
                                onClick={() => handleCoinsOnlyFilter("true")}
                            >
                                Show Session Fee Only
                            </DropdownItem>
                        </DropdownContent>
                    </Dropdown> */}
                </div>

                {data?.data && data?.data.statement_breakdown.length > 0 ? (
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton
                                classNames="bg-primary normal-case text-white *:stroke-white"
                                text="Actions"
                                stroke="#ffffff"
                            />
                        </DropdownTrigger>

                        <DropdownContent width="auto" align="end">
                            <>
                                <DropdownItem
                                    onClick={() => {
                                        handleDownload();
                                    }}
                                >
                                    Download Statement
                                </DropdownItem>

                                <DropdownItem
                                    onClick={() => {
                                        handleDialogInView("send-statement");
                                    }}
                                >
                                    Send Statement
                                </DropdownItem>
                            </>
                        </DropdownContent>
                    </Dropdown>
                ) : null}
            </div>

            {isLoading ? (
                <div className="flex flex-col gap-y-24">
                    <div className=" flex flex-col gap-y-32">
                        <Skeleton
                            height={173}
                            width="100%"
                            className="mb-24 rounded-r12"
                            containerTestId="statement-summary-loader"
                        />
                    </div>
                    <Skeleton
                        type="table"
                        containerTestId="statement-breakdown-loader"
                    />
                </div>
            ) : null}

            {error ? (
                <ListState
                    isError
                    errorMsg={
                        error?.response?.data.message ||
                        `Cannot display statement of account at this time please try again later`
                    }
                    stateHelperText="Try reloading this page to solve this issue"
                />
            ) : null}

            {data?.data && isSuccess && (
                <>
                    {data.data.statement_breakdown.length === 0 && (
                        <ListState
                            isError={false}
                            emptyMessage="No statement of account available yet"
                            stateHelperText="Statement of account will appear here once they are added"
                        />
                    )}
                    {data.data.statement_breakdown.length > 0 && (
                        <div className="flex flex-col gap-y-24">
                            {outstandingBillsData.data?.summary && (
                                <PaymentsSummary
                                    summaryData={
                                        outstandingBillsData.data?.summary
                                    }
                                />
                            )}

                            <AccountStatementList
                                statementBreakdownData={
                                    data.data.statement_breakdown
                                }
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
